import React, { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, FileText, BarChart3, Menu, LogOut, ShieldCheck,
  ListChecks, Settings as SettingsIcon, ClipboardCheck, GraduationCap, Trophy,
  LifeBuoy, ChevronDown, ChevronRight, Workflow, Sparkles, Bot, Users,
  PanelLeftClose, PanelLeftOpen,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import agentsService, { Agent } from '../services/agentsService';
import { Button, IconButton } from './ui';
import '../styles/theme.css';

// The Doc agent's full workspace is the existing Documents view; other agents route to the
// generic agent hub (same shared engine, their flow ships later).
const agentLink = (key: string) => (key === 'doc' ? '/admin/docshawk' : `/admin/agent/${key}`);

// Primary surface — what a document reviewer needs.
const NAV_PRIMARY = [
  { to: '/admin/docshawk', label: 'Documents', icon: FileText, end: false },
  { to: '/admin/assistant', label: 'Assistant', icon: Sparkles, end: false },
  { to: '/admin/parameters', label: 'Terms Checklist', icon: ListChecks, end: false },
  { to: '/admin/help', label: 'Help & Trust', icon: LifeBuoy, end: false },
];

// Advanced surface — the ML-ops / pipeline tools, collapsed by default.
const NAV_ADVANCED = [
  { to: '/admin/pipeline-hub', label: 'Pipeline', icon: Workflow, end: false },
  { to: '/admin/overview', label: 'Overview', icon: LayoutDashboard, end: false },
  { to: '/admin/evals', label: 'Evals', icon: ClipboardCheck, end: false },
  { to: '/admin/training', label: 'Training', icon: GraduationCap, end: false },
  { to: '/admin/benchmark', label: 'Benchmark', icon: Trophy, end: false },
  { to: '/admin/pipeline', label: 'Telemetry', icon: BarChart3, end: false },
  { to: '/admin/agent-access', label: 'Agent Access', icon: Users, end: false },
  { to: '/admin/settings', label: 'Settings', icon: SettingsIcon, end: false },
];

// Human breadcrumb label for the current admin route.
function crumbFor(pathname: string): string {
  if (pathname.startsWith('/admin/docshawk/runs')) return 'Run detail';
  if (pathname.startsWith('/admin/docshawk')) return 'Documents';
  if (pathname.startsWith('/admin/assistant')) return 'Assistant';
  if (pathname.startsWith('/admin/parameters')) return 'Terms Checklist';
  if (pathname.startsWith('/admin/help')) return 'Help & Trust';
  if (pathname.startsWith('/admin/evals')) return 'Evals';
  if (pathname.startsWith('/admin/training')) return 'Training';
  if (pathname.startsWith('/admin/benchmark')) return 'Benchmark';
  if (pathname.startsWith('/admin/settings')) return 'Settings';
  if (pathname.startsWith('/admin/pipeline-hub')) return 'Pipeline';
  if (pathname.startsWith('/admin/pipeline')) return 'Telemetry';
  if (pathname.startsWith('/admin/config')) return 'Pipeline config';
  if (pathname.startsWith('/admin/overview')) return 'Overview';
  return 'Documents';
}

const AppShell: React.FC = () => {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const advActive = NAV_ADVANCED.some((i) => location.pathname.startsWith(i.to))
    || location.pathname.startsWith('/admin/config');
  const [advOpen, setAdvOpen] = useState(advActive);

  // The agents this user may use (drives the Agents section). Server-filtered by RBAC.
  const [agents, setAgents] = useState<Agent[]>([]);
  useEffect(() => {
    let alive = true;
    agentsService.listMyAgents()
      .then(({ agents }) => alive && setAgents(agents))
      .catch(() => alive && setAgents([]));
    return () => { alive = false; };
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };
  const navItem = (item: { to: string; label: string; icon: any; end: boolean }) => (
    <NavLink key={item.to} to={item.to} end={item.end}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) => `mx-nav-item ${isActive ? 'active' : ''}`}
      onClick={() => setOpen(false)}>
      <item.icon size={17} strokeWidth={1.9} />
      <span className="mx-nav-item-label">{item.label}</span>
    </NavLink>
  );

  return (
    <div className="mx-app">
      <div className="mx-shell">
        <aside className={`mx-sidebar ${open ? 'open' : ''} ${collapsed ? 'collapsed' : ''}`}>
          <div className="mx-sidebar-brand">
            <span className="mx-sidebar-brand-mark"><ShieldCheck size={16} /></span>
            <span className="mx-sidebar-brand-label">DocShawk</span>
          </div>
          <nav className="mx-nav">
            {agents.length > 0 && (
              <>
                <div className="mx-nav-section">Agents</div>
                {agents.map((a) => (
                  <NavLink key={a.key} to={agentLink(a.key)}
                    title={collapsed ? a.name : undefined}
                    className={({ isActive }) => `mx-nav-item ${isActive ? 'active' : ''}`}
                    onClick={() => setOpen(false)}>
                    <Bot size={17} strokeWidth={1.9} />
                    <span className="mx-nav-item-label">{a.name}</span>
                  </NavLink>
                ))}
              </>
            )}
            {/* Doc-agent workspace tools — only for users who can use the Doc agent. */}
            {agents.some((a) => a.key === 'doc') && (
              <>
                <div className="mx-nav-section">Workspace</div>
                {NAV_PRIMARY.map(navItem)}
              </>
            )}

            {/* ML-ops / admin surfaces — admins only. */}
            {isAdmin && (
              <>
                <button className="mx-nav-item" style={{ background: 'none', border: 0,
                  cursor: 'pointer', fontWeight: 600, marginTop: 6 }}
                  title={collapsed ? 'Advanced' : undefined}
                  onClick={() => setAdvOpen((o) => !o)} aria-expanded={advOpen}>
                  {advOpen ? <ChevronDown size={17} strokeWidth={1.9} /> : <ChevronRight size={17} strokeWidth={1.9} />}
                  <span className="mx-nav-item-label">Advanced</span>
                </button>
                {advOpen && NAV_ADVANCED.map(navItem)}
              </>
            )}
          </nav>
          <div className="mx-sidebar-foot">
            <div style={{ fontWeight: 600, color: 'var(--mx-text)' }}>{user?.username}</div>
            <div>{user?.role}</div>
          </div>
          <button className="mx-collapse-btn" onClick={() => setCollapsed((c) => !c)}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
            {collapsed ? <PanelLeftOpen size={17} strokeWidth={1.9} /> : <PanelLeftClose size={17} strokeWidth={1.9} />}
            <span>Collapse</span>
          </button>
        </aside>

        <div className="mx-main">
          <header className="mx-topbar">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <IconButton icon={Menu} label="Toggle menu" onClick={() => setOpen((o) => !o)} data-mobile-only />
              <div className="mx-crumb">
                <Link to="/admin">DocShawk</Link>
                <span className="mx-subtle">/</span>
                <span className="mx-crumb-current">{crumbFor(location.pathname)}</span>
              </div>
            </div>
            <Button icon={LogOut} variant="secondary" size="sm" onClick={handleLogout}>Logout</Button>
          </header>
          <main className="mx-content">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
};

export default AppShell;
